import { gql } from '@apollo/client'
import { Banner, Button } from '@farol-ds/react'
import { useDocumentModerationModal } from '@jusbrasil-web/docview/core'
import { useEffect } from 'react'

import {
  ModerationBanner_DocumentFragment,
  ModerationBanner_ViewerFragment,
} from './__generated__/moderation-banner.graphql'
import { CONDITION_MAP, PUBLISHING_RULES_URL } from './constants'
import style from './moderation-banner.module.scss'

export interface ModerationBannerProps {
  document: ModerationBanner_DocumentFragment
  viewer: ModerationBanner_ViewerFragment | null
}

export function ModerationBanner(props: ModerationBannerProps) {
  const { document, viewer } = props
  const { accessCondition, review, docId, artifact } = document
  const bannerConfig = CONDITION_MAP.get(accessCondition)
  const documentModeration = useDocumentModerationModal()

  useEffect(() => {
    if (viewer?.user?.isAdmin) {
      documentModeration.init()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!bannerConfig) return null

  const { type, title, icon: Icon } = bannerConfig
  const reviewProfile = review?.profile

  const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  })

  const reviewDate = dateFormatter.format(new Date(review?.date || 0))

  const onModeration = () =>
    documentModeration.open({
      docId,
      artifact,
    })

  return (
    <Banner type={type} className={style.container}>
      <Banner.Content>
        <Banner.Title>
          <Banner.Icon>
            <Icon />
          </Banner.Icon>
          {title}
        </Banner.Title>
        {accessCondition === 'REVIEW' && (
          <>
            <Banner.Description>
              Obrigada por publicar no Jusbrasil! Em até 72h úteis, seu texto será analisado por
              moderadores do Jusbrasil para garantir que atende as nossas{' '}
              <a href={PUBLISHING_RULES_URL}>Regras de Publicação</a>. Caso seja aprovado, sua
              publicação será disponibilizada para toda a comunidade. Caso seja necessário realizar
              ajustes, informaremos diretamente nesta mesma página.
            </Banner.Description>
            <Banner.Description>
              Ao submeter sua publicação, você atesta que o conteúdo é verdadeiro e assume sua
              autoria, ou seja, a responsabilidade pelo seu teor.
            </Banner.Description>
          </>
        )}
        {accessCondition && ['REPROVED', 'CREATOR_ONLY', 'PRIVATE'].includes(accessCondition) && (
          <>
            <Banner.Description>
              Este documento não foi aprovado, pois não atendeu às{' '}
              <a href={PUBLISHING_RULES_URL}>regras de publicação do Jusbrasil</a>.
            </Banner.Description>
            {viewer?.user?.isAdmin && (
              <Banner.Description>
                <strong>Reprovado </strong>
                {reviewProfile ? (
                  <>
                    por <a href={reviewProfile?.url || ''}>{reviewProfile?.fullname}</a> no dia{' '}
                    {reviewDate}
                  </>
                ) : (
                  <strong>automaticamente</strong>
                )}
              </Banner.Description>
            )}
            {document.review?.comment && (
              <>
                <Banner.Description>
                  <strong>Mensagem do moderador:</strong>
                </Banner.Description>
                <Banner.Description>
                  <span dangerouslySetInnerHTML={{ __html: document.review.comment }} />
                </Banner.Description>
              </>
            )}
          </>
        )}
      </Banner.Content>
      {viewer?.user?.isAdmin && (
        <Banner.Action>
          <Button
            loading={!documentModeration.ready}
            onClick={onModeration}
            data-testid="moderation-button"
          >
            Moderar
          </Button>
        </Banner.Action>
      )}
    </Banner>
  )
}

ModerationBanner.fragments = {
  document: gql`
    fragment ModerationBanner_document on Document {
      docId
      artifact
      accessCondition
      review {
        date
        comment
        profile {
          fullname
          url
        }
      }
    }
  `,
  viewer: gql`
    fragment ModerationBanner_viewer on Profile {
      user {
        isAdmin
      }
    }
  `,
}
